import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

export function GlassCard({
  children,
  className = "",
  disableHover = false,
  ...props
}) {
  const motionProps = disableHover
    ? {}
    : {
        whileHover: {
          y: -6,
          boxShadow: "0 40px 80px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(31, 76, 84, 0.18)"
        },
        transition: { type: "spring", stiffness: 260, damping: 28 }
      };

  return (
    <motion.div
      className={cn(
        "relative rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/85 p-8 shadow-[0_30px_60px_rgba(15,23,42,0.08)] backdrop-blur",
        "transition-all duration-300 ease-out hover:border-[rgba(31,76,84,0.18)]",
        className
      )}
      {...motionProps}
      {...props}
    >
      <div className="absolute inset-0 rounded-3xl shadow-[inset_0_1px_0_rgba(255,255,255,0.75)] pointer-events-none" />
      <div className="relative z-10">{children}</div>
    </motion.div>
  );
}

export function FeatureCard({ icon: Icon, title, description, className = "" }) {
  return (
    <GlassCard className={cn("h-full space-y-4 group", className)}>
      <div className="flex items-center">
        <motion.div
          className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(31,76,84,0.08)] text-[var(--accent)] transition-all duration-300 group-hover:bg-[rgba(31,76,84,0.12)] group-hover:scale-110"
          whileHover={{ rotate: [0, -5, 5, -5, 0] }}
          transition={{ duration: 0.5 }}
        >
          <Icon className="h-5 w-5" />
        </motion.div>
      </div>
      <div className="space-y-3">
        <h3 className="text-lg font-semibold tracking-tight transition-colors group-hover:text-[var(--accent)]">{title}</h3>
        <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
          {description}
        </p>
      </div>
    </GlassCard>
  );
}

export function StatCard({ value, label, trend, className = "" }) {
  const isPositive = typeof trend === "number" && trend >= 0;

  return (
    <GlassCard className={cn("h-full space-y-4", className)}>
      <div className="eyebrow-muted">
        {label}
      </div>
      <div className="flex items-end justify-between">
        <span className="font-display text-4xl font-semibold !tracking-display text-[var(--text-primary)]">
          {value}
        </span>
        {typeof trend === "number" && (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium",
              isPositive
                ? "bg-[rgba(40,167,108,0.12)] text-success-700"
                : "bg-[rgba(220,38,38,0.12)] text-danger-700"
            )}
          >
            {isPositive ? (
              <ArrowUpRight className="h-3.5 w-3.5" />
            ) : (
              <ArrowDownRight className="h-3.5 w-3.5" />
            )}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </GlassCard>
  );
}
